import { useEffect, useRef, useState, useCallback } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { useBranding } from "../../hooks/useBranding";
import { apiUrl } from "../../utils/backendUrl";
import { attachSystemLogInterceptors } from "../../services/systemLogs";
import styles from "./AdminLayout.module.css";

// ─── Nav config ──────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { label: "Dashboard", to: "/administrator" },
  {
    label: "View Site",
    to: "/home",
    external: true,
  },
  {
    label: "Users",
    children: [
      { label: "List Users", to: "/administrator/users" },
      { label: "Add User", to: "/administrator/users/add" },
      { label: "Unvalidated Users", to: "/administrator/users/unvalidated" },
      { label: "User Fields", to: "/administrator/profilefields" },
    ],
  },
  { label: "Groups", to: "/administrator/groups" },
  { label: "Events", to: "/administrator/events" },
  { label: "Content", to: "/administrator/content" },
  { label: "Moderation", to: "/administrator/moderation" },
  { label: "Notifications", to: "/administrator/notifications" },
  {
    label: "Settings",
    children: [
      { label: "Basic Settings", to: "/administrator/settings" },
      { label: "System Logs", to: "/administrator/logs" },
      { label: "Call Quality", to: "/administrator/call-quality" },
      { label: "Menu Builder", to: "/administrator/menu-builder" },
      { label: "Custom Pages", to: "/administrator/pages" },
    ],
  },
];

// ─── Dropdown component ──────────────────────────────────────────────────────

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const skipClose = useRef(false);
  const location = useLocation();

  const isChildActive = item.children?.some((c) =>
    location.pathname === c.to || location.pathname.startsWith(c.to + "/")
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (skipClose.current) {
        skipClose.current = false;
        return;
      }
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    skipClose.current = true;
    setOpen((prev) => !prev);
  }, []);

  return (
    <div className={styles.dropdownWrapper} ref={wrapperRef}>
      <button
        className={`${styles.navItem} ${isChildActive ? styles.navItemActive : ""}`}
        onClick={handleToggle}
        type="button"
      >
        {item.label}
        <span className={styles.navItemCaret}>&#9662;</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              className={styles.dropdownItem}
              onClick={() => setOpen(false)}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AdminLayout ─────────────────────────────────────────────────────────────

export default function AdminLayout() {
  const { user } = useSelector((state) => ({ user: state.user }));
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationAccess, setNotificationAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const { logoUrl } = useBranding();

  const userInfo = user?.userinfo;
  const isAdmin =
    userInfo?.accountRole === "admin" || userInfo?.role === "admin";
  const isNotificationsRoute =
    location.pathname === "/administrator/notifications" ||
    location.pathname.startsWith("/administrator/notifications/");

  useEffect(() => {
    if (!userInfo) {
      navigate("/login", { replace: true });
      return;
    }

    if (isAdmin) {
      setNotificationAccess({
        isAdmin: true,
        canViewNotificationsAdmin: true,
        canManageConfig: true,
        canSendBulkNotifications: true,
      });
      return;
    }

    if (!isNotificationsRoute) {
      navigate("/home", { replace: true });
      return;
    }

    let active = true;
    const api = attachSystemLogInterceptors(
      axios.create({ withCredentials: true })
    );

    const loadAccess = async () => {
      setAccessLoading(true);
      try {
        const res = await api.get(apiUrl("/api/v1/admin/notifications/access"));
        const access = res.data?.data || {};
        if (!active) return;
        if (access.canViewNotificationsAdmin) {
          setNotificationAccess(access);
        } else {
          navigate("/home", { replace: true });
        }
      } catch (_error) {
        if (active) navigate("/home", { replace: true });
      } finally {
        if (active) setAccessLoading(false);
      }
    };

    loadAccess();

    return () => {
      active = false;
    };
  }, [userInfo, isAdmin, isNotificationsRoute, navigate]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const canViewNotificationsAdmin =
    isAdmin || notificationAccess?.canViewNotificationsAdmin === true;

  if (!userInfo || (isNotificationsRoute && !isAdmin && accessLoading)) {
    return (
      <div className={styles.adminShell}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedTitle}>Loading…</div>
          <div className={styles.accessDeniedText}>
            Checking your notification access.
          </div>
        </div>
      </div>
    );
  }

  if (!userInfo || (!isAdmin && !canViewNotificationsAdmin)) {
    return (
      <div className={styles.adminShell}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedTitle}>Access Denied</div>
          <div className={styles.accessDeniedText}>
            You do not have permission to view this page.
          </div>
          <Link to="/home" className={styles.backLink}>
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminShell}>
      {/* ── Header / Nav Bar ── */}
      <header className={styles.header}>
        {/* Logo */}
        <Link to="/administrator" className={styles.logoArea}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="NIHSS VDS Admin"
              className={styles.logoImg}
            />
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className={styles.nav}>
          {(isAdmin
            ? NAV_ITEMS
            : [
                {
                  label: "View Site",
                  to: "/home",
                  external: true,
                },
                { label: "Notifications", to: "/administrator/notifications" },
              ]
          ).map((item) => {
            if (item.children) {
              return <NavDropdown key={item.label} item={item} />;
            }

            if (item.external) {
              return (
                <Link key={item.to} to={item.to} className={styles.navItem}>
                  {item.label}
                </Link>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/administrator"}
                className={({ isActive }) =>
                  `${styles.navItem} ${isActive ? styles.navItemActive : ""}`
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Mobile hamburger */}
        <button
          className={styles.hamburger}
          onClick={() => setMobileOpen((prev) => !prev)}
          type="button"
          aria-label="Toggle navigation"
        >
          {mobileOpen ? "\u2715" : "\u2630"}
        </button>
      </header>

      {/* ── Mobile Nav Overlay ── */}
      <div
        className={`${styles.mobileOverlay} ${mobileOpen ? styles.open : ""}`}
        onClick={() => setMobileOpen(false)}
      />
      <div className={`${styles.mobileMenu} ${mobileOpen ? styles.open : ""}`}>
        {(isAdmin
          ? NAV_ITEMS
          : [
              {
                label: "View Site",
                to: "/home",
                external: true,
              },
              { label: "Notifications", to: "/administrator/notifications" },
            ]
        ).map((item) => {
          if (item.children) {
            return (
              <div key={item.label}>
                <div className={styles.mobileGroupLabel}>{item.label}</div>
                {item.children.map((child) => (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    className={({ isActive }) =>
                      `${styles.mobileNavItem} ${styles.mobileSubItem} ${
                        isActive ? styles.mobileNavItemActive : ""
                      }`
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            );
          }

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/administrator"}
              className={({ isActive }) =>
                `${styles.mobileNavItem} ${
                  isActive ? styles.mobileNavItemActive : ""
                }`
              }
              onClick={() => setMobileOpen(false)}
            >
              {item.label}
            </NavLink>
          );
        })}
      </div>

      {/* ── Content ── */}
      <main className={styles.content}>
        <Outlet context={{ notificationAccess }} />
      </main>
    </div>
  );
}
